// "Set your GSTIN / state" on the till (gst-parked task 030, owner decisions
// D4/D5). India GST regime only. Mirrors apps/admin/src/lib/gst-setup.ts so the
// till and admin show the same text; the API works out the same "registered"
// answer (common/utils/gst-registration.ts) and sends it on /auth/me.

export interface GstSetupInput {
  isIndia: boolean;
  /** The owner's stored choice; null / undefined = derived from the GSTIN. */
  setting: boolean | null | undefined;
  gstin: string | null | undefined;
  state: string | null | undefined;
  locations: ReadonlyArray<{ name: string; is_active: boolean; state?: string | null }>;
}

export type GstSetupWarning =
  /** No GSTIN and no choice made: Orio360 treats the business as not registered. */
  | { kind: 'UNREGISTERED_NO_GSTIN' }
  /** Registered, but something the tax type needs is missing. */
  | { kind: 'MISSING'; gstin: boolean; state: boolean; locations: string[] };

export function gstSetupWarning(input: GstSetupInput): GstSetupWarning | null {
  if (!input.isIndia) return null;
  const registered =
    input.setting === true || input.setting === false ? input.setting : !!input.gstin?.trim();
  if (!registered) {
    // An owner who said "not registered" has nothing to fix.
    return input.setting === false ? null : { kind: 'UNREGISTERED_NO_GSTIN' };
  }
  const gstin = !input.gstin?.trim();
  const state = !input.state?.trim();
  const locations = input.locations
    .filter((l) => l.is_active && !l.state?.trim())
    .map((l) => l.name);
  return gstin || state || locations.length > 0
    ? { kind: 'MISSING', gstin, state, locations }
    : null;
}

/**
 * Beside the till's IGST switch when the store's split is only the same-state
 * default — a GSTIN / State is missing (gst-parked 080, D4).
 */
export const SUPPLY_DEFAULT_WARNING =
  'Set your GSTIN / state. Until then, Orio360 bills each sale as same-state, with CGST and SGST.';

/** Title and short sentences for a warning (age-10 English). */
export function gstSetupText(w: GstSetupWarning): { title: string; lines: string[] } {
  if (w.kind === 'UNREGISTERED_NO_GSTIN') {
    return {
      title: 'Not GST registered',
      lines: [
        'You have not added a GSTIN, so Orio360 treats your business as not GST registered.',
        'Are you registered? Add your GSTIN and your State.',
      ],
    };
  }
  const lines: string[] = [];
  if (w.gstin) lines.push('Add your GSTIN. It goes on every bill.');
  if (w.state) lines.push('Pick the State your business is in.');
  if (w.locations.length > 0)
    lines.push(`Pick a State for these stores: ${w.locations.join(', ')}.`);
  lines.push('Until then, Orio360 bills each sale as same-state, with CGST and SGST.');
  return { title: 'Set your GSTIN / state', lines };
}
